import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, Typography, makeStyles } from '@material-ui/core'
import type { SpecIntelligenceReport } from 'api/graphQL/types/specIntelligence.types'
import SpecIntelligenceFindingCard from 'components/SpecIntelligence/SpecIntelligenceFindingCard/SpecIntelligenceFindingCard'
import React, { useMemo, useState } from 'react'
import InsightsRunDetailHeader from './InsightsRunDetailHeader'
import InsightsRunFilterBar, { CategoryFilterValue, StateFilterValue } from './InsightsRunFilterBar'

const CARD_SHADOW = '0 1px 2px 0 rgba(15,23,42,0.05)'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#F7F8FA',
    },
    containerInner: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
        minHeight: 0,
        width: '100%',
        maxWidth: pxToRem(1920),
        margin: '0 auto',
        padding: theme.spacing(2.5, 2.5, 0, 2.5),
    },
    scrollArea: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
    },
    headerCard: {
        flexShrink: 0,
        backgroundColor: theme.palette.common.white,
        borderRadius: pxToRem(10),
        boxShadow: CARD_SHADOW,
        marginBottom: theme.spacing(2),
        overflow: 'hidden',
    },
    findingsCard: {
        flexShrink: 0,
        backgroundColor: theme.palette.common.white,
        borderRadius: pxToRem(10),
        boxShadow: CARD_SHADOW,
        overflow: 'hidden',
        marginBottom: theme.spacing(2.5),
    },
    findingsList: {
        padding: theme.spacing(1.75),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    emptyState: {
        padding: theme.spacing(4),
        textAlign: 'center',
        color: '#757575',
        fontSize: pxToRem(13),
    },
}))

/** Props for {@link InsightsRunDetail}. */
export interface InsightsRunDetailProps {
    /** The currently selected run, including its full findings. */
    report: SpecIntelligenceReport
    /** The current project id, passed through to findings for the "Go to section" link. */
    projectId: string
}

/** Metadata header, Category/State filter row, and filtered findings list for the selected run. */
const InsightsRunDetail = ({ report, projectId }: InsightsRunDetailProps) => {
    const classes = useStyles()
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilterValue>('All')
    const [stateFilter, setStateFilter] = useState<StateFilterValue>('All')

    const filteredFindings = useMemo(
        () =>
            report.findings.filter(
                (finding) =>
                    (categoryFilter === 'All' || finding.category === categoryFilter) &&
                    (stateFilter === 'All' || finding.resolutionState === stateFilter)
            ),
        [report.findings, categoryFilter, stateFilter]
    )

    return (
        <div className={classes.container}>
            <div className={classes.containerInner}>
                <div className={classes.headerCard}>
                    <InsightsRunDetailHeader report={report} />
                    <InsightsRunFilterBar
                        findings={report.findings}
                        categoryFilter={categoryFilter}
                        stateFilter={stateFilter}
                        onCategoryChange={setCategoryFilter}
                        onStateChange={setStateFilter}
                    />
                </div>
                <div className={classes.scrollArea} tabIndex={0}>
                    <div className={classes.findingsCard}>
                        {filteredFindings.length === 0 ? (
                            <Typography className={classes.emptyState}>No findings match your filters.</Typography>
                        ) : (
                            <div className={classes.findingsList}>
                                {filteredFindings.map((finding) => (
                                    <SpecIntelligenceFindingCard key={finding.id} finding={finding} readOnly projectId={projectId} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default InsightsRunDetail
